/**
 * Per-tour layer-visibility map + custom-preset list. Same JSON the viewer
 * reads from for the bottom-left layer selector.
 *
 * Storage: gs://{bucket}/tours/{tourId}/layer-settings.json
 *   {
 *     enabledLayers: { [layerKey]: boolean },
 *     customPresets: [{ name, prompt, createdAt }],
 *     updatedAt
 *   }
 *
 * Backed by the standalone `tourLayerSettings` Cloud Function (gen2,
 * id3d-prod). Anonymous-CORS — same convention as the rest of the AI
 * cloud-functions family. Reads bypass the function and hit the public
 * GCS object directly so we don't pay cold-start latency on every load.
 */

export interface CustomPreset {
  name: string;
  prompt: string;
  createdAt?: string;
}

export interface LayerSettings {
  enabledLayers: Record<string, boolean>;
  customPresets: CustomPreset[];
  /** When true, the viewer hides its bottom-left layer selector and the
   *  dashboard's iframe + Share URL get `&hideLayers=1` appended. */
  hideLayers?: boolean;
  updatedAt?: string;
}

const STORAGE_BUCKET =
  process.env.REACT_APP_STORAGE_BUCKET || 'id3d-prod.appspot.com';

function stripPrefix(tourId: string): string {
  return tourId.startsWith('tours/') ? tourId.slice(6) : tourId;
}

/**
 * Read straight from the public GCS object (faster than going through the
 * function for reads). The function writes with `cache-control: no-cache`
 * so the cache-buster query is enough to defeat any CDN copy.
 */
export async function getTourLayerSettings(
  tourId: string,
): Promise<LayerSettings | null> {
  const id = stripPrefix(tourId);
  const url = `https://storage.googleapis.com/${STORAGE_BUCKET}/tours/${id}/layer-settings.json?_=${Date.now()}`;
  const response = await fetch(url, { method: 'GET' });
  if (response.status === 404) return null;
  if (!response.ok) {
    throw new Error(
      `tour layer settings GET returned ${response.status} ${response.statusText}`,
    );
  }
  const data = (await response.json()) as Partial<LayerSettings>;
  return {
    enabledLayers: data.enabledLayers ?? {},
    customPresets: data.customPresets ?? [],
    hideLayers: data.hideLayers ?? false,
    updatedAt: data.updatedAt,
  };
}

interface LayerSettingsPatch {
  enabledLayers?: Record<string, boolean>;
  customPresets?: CustomPreset[];
  hideLayers?: boolean;
}

/**
 * Server-side merge: any field omitted from `patch` is preserved on the
 * existing document. So a visibility-only update and a presets-only update
 * compose correctly even if they race.
 */
export async function setTourLayerSettings(
  tourId: string,
  patch: LayerSettingsPatch,
): Promise<void> {
  const endpoint = process.env.REACT_APP_LAYER_SETTINGS_API_URL;
  if (!endpoint) {
    throw new Error('REACT_APP_LAYER_SETTINGS_API_URL is not configured');
  }
  const body: Record<string, unknown> = { tourId: stripPrefix(tourId) };
  if (patch.enabledLayers !== undefined) {
    body.enabledLayers = patch.enabledLayers;
  }
  if (patch.customPresets !== undefined) {
    body.customPresets = patch.customPresets;
  }
  if (patch.hideLayers !== undefined) {
    body.hideLayers = patch.hideLayers;
  }
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    let detail = '';
    try {
      const errBody = await response.json();
      detail = errBody?.error || '';
    } catch {
      /* ignore */
    }
    throw new Error(
      detail ||
        `tour layer settings POST returned ${response.status} ${response.statusText}`,
    );
  }
}

const RESERVED_PRESET_NAMES = new Set([
  'defurnish',
  'declutter',
  'colorization',
]);
const PRESET_NAME_RE = /^[a-z0-9][a-z0-9_-]*$/;

/**
 * Mirrors the server-side rules so the form can show inline errors before
 * round-tripping. Returns `null` when the name is valid, otherwise a
 * human-readable error message.
 */
export function validatePresetName(
  name: string,
  existing: CustomPreset[],
): string | null {
  if (!name) return 'Name is required';
  if (name.length > 64) return 'Name must be 64 characters or fewer';
  if (!PRESET_NAME_RE.test(name)) {
    return 'Use only lowercase letters, digits, hyphens, and underscores (must start with a letter or digit)';
  }
  if (RESERVED_PRESET_NAMES.has(name)) {
    return `"${name}" is a reserved name`;
  }
  if (existing.some((p) => p.name === name)) {
    return `A variant named "${name}" already exists`;
  }
  return null;
}

/**
 * Visibility getter that bakes the "original defaults to enabled" rule.
 * Use this everywhere instead of reading `enabledLayers[key]` directly so
 * the default isn't reimplemented inconsistently.
 */
export function isLayerEnabled(
  enabledLayers: Record<string, boolean>,
  key: string,
): boolean {
  if (key === 'original') return enabledLayers[key] !== false;
  return enabledLayers[key] === true;
}
